import { call, takeLatest } from 'redux-saga/effects';
import { actions } from '@restore/PMTaskDefinition/PMTaskDefinition.slice';
import { dispatch, Resp } from '@restore/helpers';
import * as api from '@api/pmTaskDefinitions';
import {
  goToRoute,
  redirectToCTListOnError,
  runToast,
  showErrorToast,
} from '@restore/tools';
import {
  PMTaskDefinitionCreateAction,
  PMTaskDefinitionDeleteAction,
  PMTaskDefinitionFetchAction,
  PMTaskDefinitionUpdateAction,
} from '@restore/PMTaskDefinition/PMTaskDefinition.type';
import { prepareUrl } from '@utils/routerHelpers';
import { routesPath } from '@router/routes';

export function* PMTaskDefinitionsWorker(action: PMTaskDefinitionFetchAction) {
  try {
    const { data }: Resp<typeof api.fetchPMTaskDefinitions> = yield call(
      api.fetchPMTaskDefinitions,
      action.payload.clinicalTrialId
    );

    const sortedData = data.sort((a, b) => {
      if (a.taskDefinitionOrder < b.taskDefinitionOrder) return -1;
      if (a.taskDefinitionOrder > b.taskDefinitionOrder) return 1;
      return 0;
    });

    yield dispatch(actions.fetchPMTaskDefinitionsSuccess(sortedData));
  } catch (error) {
    yield call(redirectToCTListOnError, error);
    yield dispatch(actions.fetchPMTaskDefinitionsFailure(error as Error));
  }
}

export function* createPMTaskDefinitionWorker({
  payload,
}: PMTaskDefinitionCreateAction) {
  try {
    yield call(
      api.createPMTaskDefinition,
      payload.clinicalTrialId,
      payload.data
    );
    yield dispatch(actions.createPMTaskDefinitionSuccess(null));
    yield call(runToast, 'success', 'Task created successfully');
    yield goToRoute(
      prepareUrl(routesPath.taskDefinition, [payload.clinicalTrialId])
    );
  } catch (error) {
    yield dispatch(actions.createPMTaskDefinitionFailure(error as Error));
    yield showErrorToast(error, 'Failed to create task');
  }
}

export function* updatePMTaskDefinitionWorker({
  payload,
}: PMTaskDefinitionUpdateAction) {
  try {
    const response: Resp<typeof api.updatePMTaskDefinition> = yield call(
      api.updatePMTaskDefinition,
      payload.taskDefinitionId,
      payload.clinicalTrialId,
      payload.data
    );
    yield dispatch(actions.updatePMTaskDefinitionSuccess(response.data));
    yield call(runToast, 'success', 'Task updated successfully');
    yield goToRoute(
      prepareUrl(routesPath.taskDefinition, [payload.clinicalTrialId])
    );
  } catch (error) {
    yield dispatch(actions.updatePMTaskDefinitionFailure(error as Error));
    yield showErrorToast(error, 'Failed to upload task.');
  }
}

export function* deletePMTaskDefinitionWorker({
  payload,
}: PMTaskDefinitionDeleteAction) {
  try {
    yield call(
      api.deletePMTaskDefinition,
      payload.taskDefinitionId,
      payload.clinicalTrialId
    );
    yield dispatch(actions.deletePMTaskDefinitionsSuccess(null));
    const response: Resp<typeof api.fetchPMTaskDefinitions> = yield call(
      api.fetchPMTaskDefinitions,
      payload.clinicalTrialId
    );
    yield dispatch(actions.fetchPMTaskDefinitionsSuccess(response.data));
    yield call(runToast, 'success', 'Task deleted successfully');
  } catch (error) {
    yield dispatch(actions.deletePMTaskDefinitionsFailure(error as Error));
    yield showErrorToast(error, 'Failed to delete task.');
  }
}

export function* PMTaskDefinitionsSaga() {
  yield takeLatest(
    actions.fetchPMTaskDefinitions.type,
    PMTaskDefinitionsWorker
  );
  yield takeLatest(
    actions.createPMTaskDefinition.type,
    createPMTaskDefinitionWorker
  );
  yield takeLatest(
    actions.updatePMTaskDefinition.type,
    updatePMTaskDefinitionWorker
  );
  yield takeLatest(
    actions.deletePMTaskDefinition.type,
    deletePMTaskDefinitionWorker
  );
}
